import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import {
  CheckCircle2, ExternalLink, Loader2, RefreshCw, ShieldCheck, AlertCircle, Send,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const publicApi = axios.create({ baseURL: `${BACKEND}/api/kyc-links/public`, timeout: 20000 });

/**
 * /onboard/:token — public KYC onboarding page (Phase 7.37r refresh).
 *
 * Flow mirrors the partner-facing Customer KYC wizard:
 *   1. Show link metadata + current verification %.
 *   2. If the target's hitch.zone session is live → fetch a REAL DigiLocker URL
 *      and offer "Open hitch.zone KYC" — same DigiLocker that customer-kyc uses.
 *   3. Otherwise → ask the target to send an OTP to their mobile (via hitch.zone)
 *      and enter it inline. On verify we cache the bearer; URL fetch succeeds.
 *   4. After the user finishes DigiLocker in the new tab, "Refresh" repulls
 *      the verification % from hitch.zone and auto-completes the link.
 */
export default function OnboardKyc() {
  const { token } = useParams();
  const [meta, setMeta] = useState(null);
  const [kyc, setKyc] = useState(null);
  const [hitchUrl, setHitchUrl] = useState(null);
  const [needsOtp, setNeedsOtp] = useState(false);
  const [otp, setOtp] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [otpSending, setOtpSending] = useState(false);
  // Phase 7.37t — PAN capture flow
  const [otpVerified, setOtpVerified] = useState(false);
  const [pan, setPan] = useState("");
  const [panSubmitting, setPanSubmitting] = useState(false);
  const [panResult, setPanResult] = useState(null); // {pan_number, full_name}
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [urlLoading, setUrlLoading] = useState(false);
  const [error, setError] = useState(null);
  const [info, setInfo] = useState(null);

  const loadMeta = async () => {
    try {
      const { data } = await publicApi.get(`/${token}`);
      setMeta(data);
      if (data.last_kyc_percentage !== null && data.last_kyc_percentage !== undefined) {
        setKyc({ verification_percentage: data.last_kyc_percentage, status: data.status });
      }
    } catch (e) {
      setError(e?.response?.data?.detail || "Link not found or expired");
    } finally {
      setLoading(false);
    }
  };

  // Try to fetch a real DigiLocker URL — if the parent session is missing,
  // backend returns `needs_otp: true` and we flip to the OTP step.
  const loadHitchUrl = async () => {
    setUrlLoading(true);
    try {
      const { data } = await publicApi.get(`/${token}/hitch-onboard-url`);
      if (data.needs_otp) {
        setNeedsOtp(true);
        setHitchUrl(null);
      } else if (data.url) {
        setHitchUrl(data.url);
        setNeedsOtp(false);
      }
    } catch {
      setNeedsOtp(true);
    } finally {
      setUrlLoading(false);
    }
  };

  const sendOtp = async () => {
    setOtpSending(true);
    setInfo(null);
    setError(null);
    try {
      const { data } = await publicApi.post(`/${token}/send-otp`);
      setOtpSent(true);
      setInfo(data.cooldown ? data.message : `OTP sent to +91 ${meta.mobile}. Enter the 4-6 digit code below.`);
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not send OTP. Try again.");
    } finally {
      setOtpSending(false);
    }
  };

  const verifyOtp = async () => {
    setVerifying(true);
    setError(null);
    try {
      await publicApi.post(`/${token}/verify-otp`, { otp });
      setOtp("");
      setNeedsOtp(false);
      setOtpSent(false);
      setOtpVerified(true);                       // <- now ask for PAN
      setInfo("OTP verified. Enter your PAN to complete KYC.");
    } catch (e) {
      setError(e?.response?.data?.detail || "Invalid OTP");
    } finally {
      setVerifying(false);
    }
  };

  const submitPan = async () => {
    const normalised = pan.trim().toUpperCase();
    if (!/^[A-Z]{5}[0-9]{4}[A-Z]$/.test(normalised)) {
      setError("PAN must be in format ABCDE1234F");
      return;
    }
    setPanSubmitting(true);
    setError(null);
    try {
      const { data } = await publicApi.post(`/${token}/submit-pan`, { pan_number: normalised });
      setPanResult({ pan_number: data.pan_number, full_name: data.full_name });
      setKyc({
        verification_percentage: data.verification_percentage,
        is_verified: data.is_verified,
      });
      setInfo(`PAN verified — welcome ${data.full_name}!`);
      // Try to load the DigiLocker URL now (parent session is live)
      await loadHitchUrl();
    } catch (e) {
      setError(e?.response?.data?.detail || "PAN verification failed");
    } finally {
      setPanSubmitting(false);
    }
  };

  const refreshKyc = async () => {
    setRefreshing(true);
    try {
      const { data } = await publicApi.post(`/${token}/refresh-kyc`);
      setKyc(data);
      setMeta((m) => m ? { ...m, status: data.status, last_kyc_percentage: data.verification_percentage } : m);
    } catch (e) {
      setError(e?.response?.data?.detail || "Refresh failed");
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => {
    loadMeta();
    loadHitchUrl();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-muted/30">
        <Loader2 className="size-6 animate-spin text-primary" />
      </div>
    );
  }

  if (error && !meta) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-muted/30 p-6">
        <Card className="max-w-md w-full">
          <CardHeader className="text-center">
            <AlertCircle className="size-10 mx-auto text-rose-600" />
            <CardTitle>Link unavailable</CardTitle>
            <CardDescription>{error}</CardDescription>
          </CardHeader>
          <CardContent className="text-center text-sm text-muted-foreground">
            Please contact the partner who sent you this link to issue a new one.
          </CardContent>
        </Card>
      </div>
    );
  }

  const pct = kyc?.verification_percentage ?? meta.last_kyc_percentage ?? 0;
  const threshold = kyc?.threshold ?? 50;
  const isVerified = pct >= threshold;
  const isCompleted = meta.status === "completed" || (kyc?.is_verified);

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary/5 via-background to-muted/30 py-10 px-4">
      <div className="max-w-2xl mx-auto space-y-6">
        <div className="text-center">
          <ShieldCheck className="size-12 mx-auto text-primary" />
          <h1 className="mt-3 text-2xl sm:text-3xl font-bold tracking-tight">PayFlex onboarding</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {meta.partner_display_name} has invited you to complete KYC{meta.include_bank ? " + add a bank account" : ""}.
          </p>
        </div>

        <Card data-testid="onboard-meta-card">
          <CardHeader>
            <CardTitle className="text-base flex items-center justify-between gap-2">
              <span>Your link</span>
              <Badge variant="outline" className="text-[10px] uppercase">{meta.purpose}</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="text-sm space-y-2">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Mobile</span>
              <span className="font-mono">+91 {meta.mobile}</span>
            </div>
            {meta.partner_note && (
              <div className="rounded-md bg-muted/40 p-2 text-xs italic" data-testid="onboard-partner-note">
                {meta.partner_note}
              </div>
            )}
          </CardContent>
        </Card>

        {isCompleted ? (
          <Card className="border-emerald-300 bg-emerald-50" data-testid="onboard-completed">
            <CardHeader className="text-center">
              <CheckCircle2 className="size-12 mx-auto text-emerald-600" />
              <CardTitle className="text-emerald-900">KYC complete</CardTitle>
              <CardDescription className="text-emerald-800">
                You're at {pct}% verified — your partner has been notified. You can close this page now.
              </CardDescription>
            </CardHeader>
          </Card>
        ) : (
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Complete KYC via hitch.zone</CardTitle>
              <CardDescription>
                You're at <span className="font-mono">{pct}%</span>. You need at least <span className="font-mono">{threshold}%</span>. Verify your PAN and Aadhaar via DigiLocker on hitch.zone, then come back and tap Refresh.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="rounded-md border-2 border-primary/40 bg-primary/5 p-4 text-center" data-testid="onboard-kyc-status">
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Verification</div>
                <div className="text-3xl font-bold font-mono mt-1">
                  {pct}% <span className="text-base text-muted-foreground">/ {threshold}%</span>
                </div>
              </div>

              {info && (
                <div className="rounded-md border border-emerald-200 bg-emerald-50 text-emerald-900 text-xs px-3 py-2" data-testid="onboard-info">
                  {info}
                </div>
              )}
              {error && (
                <div className="rounded-md border border-rose-200 bg-rose-50 text-rose-900 text-xs px-3 py-2" data-testid="onboard-error">
                  {error}
                </div>
              )}

              {/* Branch A — we already have a live hitch session: show the deep link */}
              {hitchUrl ? (
                <a
                  href={hitchUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center justify-center w-full rounded-md bg-primary text-primary-foreground hover:bg-primary/90 px-4 py-3 text-sm font-medium gap-2"
                  data-testid="onboard-hitch-link"
                >
                  Open hitch.zone KYC <ExternalLink className="size-4" />
                </a>
              ) : needsOtp ? (
                /* Branch B — no session: capture OTP first */
                <div className="space-y-3 rounded-md border border-border bg-muted/20 p-4" data-testid="onboard-otp-block">
                  <div className="text-xs font-medium">Verify it's you</div>
                  <p className="text-xs text-muted-foreground">
                    Tap "Send OTP" to receive a 4-6 digit code on <span className="font-mono">+91 {meta.mobile}</span>. After verifying, the DigiLocker link opens automatically.
                  </p>
                  {!otpSent ? (
                    <Button
                      onClick={sendOtp}
                      disabled={otpSending}
                      className="w-full"
                      data-testid="onboard-send-otp"
                    >
                      {otpSending ? <Loader2 className="size-4 mr-2 animate-spin" /> : <Send className="size-4 mr-2" />}
                      {otpSending ? "Sending…" : "Send OTP"}
                    </Button>
                  ) : (
                    <>
                      <div className="space-y-2">
                        <Label htmlFor="otp" className="text-xs">Enter OTP</Label>
                        <Input
                          id="otp"
                          inputMode="numeric"
                          maxLength={6}
                          value={otp}
                          onChange={(e) => setOtp(e.target.value.replace(/[^0-9]/g, ""))}
                          placeholder="123456"
                          className="font-mono text-center text-lg tracking-widest"
                          data-testid="onboard-otp-input"
                        />
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          onClick={verifyOtp}
                          disabled={verifying || otp.length < 4}
                          className="flex-1"
                          data-testid="onboard-verify-otp"
                        >
                          {verifying ? <Loader2 className="size-4 mr-2 animate-spin" /> : null}
                          {verifying ? "Verifying…" : "Verify & continue"}
                        </Button>
                        <Button
                          variant="outline"
                          onClick={sendOtp}
                          disabled={otpSending}
                          data-testid="onboard-resend-otp"
                        >
                          Resend
                        </Button>
                      </div>
                    </>
                  )}
                </div>
              ) : urlLoading ? (
                <div className="text-center text-xs text-muted-foreground py-2">
                  <Loader2 className="size-4 mx-auto animate-spin" />
                </div>
              ) : null}

              {/* Phase 7.37t — PAN capture step (mirrors customer-kyc wizard) */}
              {otpVerified && !panResult && (
                <div className="space-y-3 rounded-md border border-primary/30 bg-primary/5 p-4" data-testid="onboard-pan-block">
                  <div className="text-xs font-medium flex items-center gap-1.5">
                    <ShieldCheck className="size-3.5 text-primary" /> Verify your PAN
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Enter your 10-character PAN. We verify it through our regulated parent system (hitch.zone) — your PAN is never stored as an image.
                  </p>
                  <div className="space-y-2">
                    <Label htmlFor="pan" className="text-xs">PAN number</Label>
                    <Input
                      id="pan"
                      value={pan}
                      onChange={(e) => setPan(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 10))}
                      placeholder="ABCDE1234F"
                      maxLength={10}
                      className="font-mono uppercase tracking-widest text-base"
                      data-testid="onboard-pan-input"
                    />
                  </div>
                  <Button
                    onClick={submitPan}
                    disabled={panSubmitting || pan.length !== 10}
                    className="w-full"
                    data-testid="onboard-submit-pan"
                  >
                    {panSubmitting ? <Loader2 className="size-4 mr-2 animate-spin" /> : null}
                    {panSubmitting ? "Verifying PAN…" : "Verify PAN"}
                  </Button>
                </div>
              )}

              {/* Phase 7.37t — PAN verified summary */}
              {panResult && (
                <div className="rounded-md border border-emerald-300 bg-emerald-50 p-4" data-testid="onboard-pan-verified">
                  <div className="flex items-start gap-2">
                    <CheckCircle2 className="size-4 text-emerald-600 mt-0.5 shrink-0" />
                    <div className="text-xs text-emerald-900 space-y-1 min-w-0">
                      <div className="font-medium">PAN verified</div>
                      <div className="flex justify-between gap-2">
                        <span className="opacity-70">Name</span>
                        <span className="font-medium truncate">{panResult.full_name}</span>
                      </div>
                      <div className="flex justify-between gap-2">
                        <span className="opacity-70">PAN</span>
                        <span className="font-mono">{panResult.pan_number}</span>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <Button
                variant="outline"
                onClick={refreshKyc}
                disabled={refreshing}
                className="w-full"
                data-testid="onboard-refresh-btn"
              >
                <RefreshCw className={`size-4 mr-1 ${refreshing ? "animate-spin" : ""}`} />
                {refreshing ? "Refreshing…" : "I've completed KYC — refresh status"}
              </Button>
              <p className="text-[11px] text-muted-foreground text-center">
                Verification is done by hitch.zone, our regulated parent system. PayFlex never stores your PAN or Aadhaar documents — only the verification % and your full name.
              </p>
            </CardContent>
          </Card>
        )}

        {isVerified && meta.include_bank && !isCompleted && (
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Step 2 · Add your bank account</CardTitle>
              <CardDescription>
                Add a bank account at hitch.zone to start receiving payments. The partner will see your verified account automatically.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {hitchUrl && (
                <a
                  href={hitchUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center justify-center w-full rounded-md bg-primary text-primary-foreground hover:bg-primary/90 px-4 py-3 text-sm font-medium gap-2"
                  data-testid="onboard-bank-link"
                >
                  Add bank account at hitch.zone <ExternalLink className="size-4" />
                </a>
              )}
            </CardContent>
          </Card>
        )}

        <p className="text-center text-[11px] text-muted-foreground">
          Powered by PayFlex · KYC verified via hitch.zone
        </p>
      </div>
    </div>
  );
}
